import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { storiesAPI } from "../../services/api";
import "./StorySubmitForm.css";

const ease   = [0.22, 1, 0.36, 1];
const fadeUp = (d = 0) => ({ hidden:{ opacity:0, y:16 }, show:{ opacity:1, y:0, transition:{ duration:0.55, ease, delay:d } } });

const CATEGORIES = [
  { value:"veteran",         label:"Veteran" },
  { value:"first_responder", label:"First Responder" },
  { value:"law_enforcement", label:"Law Enforcement" },
  { value:"gold_star",       label:"Gold Star Family" },
  { value:"volunteer",       label:"Volunteer" },
  { value:"donor",           label:"Donor" },
];

const RATINGS = [1,2,3,4,5];

function Field({ label, required, error, children }) {
  return (
    <div className="ssf__field">
      <label className="ssf__label">{label}{required && <span className="ssf__req"> *</span>}</label>
      {children}
      {error && <p className="ssf__field-error">{error}</p>}
    </div>
  );
}

export default function StorySubmitForm({ onClose }) {
  const [step, setStep]       = useState(1); // 1=personal, 2=location, 3=story
  const [form, setForm]       = useState({
    first_name:"", last_name_initial:"", category:"", email:"",
    city:"", country:"USA", retreat_name:"", rating:5, quote:"",
    // location picked on map or typed
    lng:"", lat:"",
  });
  const [errors, setErrors]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [submitErr, setSubmitErr]   = useState("");

  const set = (k, v) => setForm(prev => ({ ...prev, [k]:v }));

  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!form.first_name.trim()) e.first_name = "First name is required.";
      if (!form.category)          e.category   = "Please select your category.";
    }
    if (step === 2) {
      if (!form.city.trim())       e.city = "City is required.";
      if (!form.lng || !form.lat)  e.location = "Please enter coordinates or click 'Use My Location'.";
    }
    if (step === 3) {
      if (!form.quote.trim())      e.quote = "Your story / quote is required.";
      if (form.quote.trim().length < 20) e.quote = "Please share at least 20 characters.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => { setErrors({}); setStep(s => s - 1); };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      set("lat", pos.coords.latitude.toFixed(6));
      set("lng", pos.coords.longitude.toFixed(6));
    }, () => {
      setErrors(prev => ({ ...prev, location:"Could not access location. Please enter coordinates manually." }));
    });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true); setSubmitErr("");
    try {
      await storiesAPI.submit({
        first_name:       form.first_name.trim(),
        last_name_initial:form.last_name_initial.trim().toUpperCase().slice(0,1),
        category:         form.category,
        city:             form.city.trim(),
        country:          form.country || "USA",
        lng:              parseFloat(form.lng),
        lat:              parseFloat(form.lat),
        retreat_name:     form.retreat_name.trim() || undefined,
        rating:           parseInt(form.rating) || 5,
        quote:            form.quote.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitErr(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ssf__overlay" onClick={onClose}>
        <motion.div className="ssf__modal" onClick={e => e.stopPropagation()}
          initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}>
          <div className="ssf__success">
            <div className="ssf__success-icon">🗺️</div>
            <h2 className="ssf__success-title">Story Submitted!</h2>
            <p className="ssf__success-text">
              Thank you for sharing your journey. Our team will review your story and once approved, it will appear on the community map.
            </p>
            <p className="ssf__success-note">This usually takes 1–3 business days.</p>
            <button type="button" className="ssf__cta-btn" onClick={onClose}>Close</button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="ssf__overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="ssf__modal" onClick={e => e.stopPropagation()}
        initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.4, ease }}>

        {/* Header */}
        <div className="ssf__head">
          <div>
            <h2 className="ssf__title">Share Your Story</h2>
            <p className="ssf__sub">Join the community map and inspire others who serve.</p>
          </div>
          <button type="button" className="ssf__close" onClick={onClose}>✕</button>
        </div>

        {/* Progress */}
        <div className="ssf__progress">
          {[
            { n:1, label:"About You" },
            { n:2, label:"Location" },
            { n:3, label:"Your Story" },
          ].map(s => (
            <div key={s.n} className={`ssf__step ${step === s.n ? "ssf__step--active" : step > s.n ? "ssf__step--done" : ""}`}>
              <div className="ssf__step-dot">{step > s.n ? "✓" : s.n}</div>
              <span>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Form body */}
        <div className="ssf__body">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" variants={fadeUp()} initial="hidden" animate="show" exit={{ opacity:0 }}>
                <div className="ssf__row">
                  <Field label="First Name" required error={errors.first_name}>
                    <input className="ssf__input" placeholder="Your first name"
                      value={form.first_name} onChange={e => set("first_name", e.target.value)} />
                  </Field>
                  <Field label="Last Initial">
                    <input className="ssf__input ssf__input--small" placeholder="D"
                      value={form.last_name_initial} onChange={e => set("last_name_initial", e.target.value)} maxLength={1} />
                  </Field>
                </div>

                <Field label="Category" required error={errors.category}>
                  <div className="ssf__cat-grid">
                    {CATEGORIES.map(cat => (
                      <button key={cat.value} type="button"
                        className={`ssf__cat-btn ${form.category === cat.value ? "ssf__cat-btn--active" : ""}`}
                        onClick={() => set("category", cat.value)}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Email (private — not shown on map)">
                  <input className="ssf__input" type="email" placeholder="your@email.com"
                    value={form.email} onChange={e => set("email", e.target.value)} />
                  <p className="ssf__input-hint">Optional. We'll notify you when your story is approved.</p>
                </Field>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" variants={fadeUp()} initial="hidden" animate="show" exit={{ opacity:0 }}>
                <Field label="City / Town" required error={errors.city}>
                  <input className="ssf__input" placeholder="e.g. Austin"
                    value={form.city} onChange={e => set("city", e.target.value)} />
                </Field>
                <Field label="Country">
                  <input className="ssf__input" placeholder="USA"
                    value={form.country} onChange={e => set("country", e.target.value)} />
                </Field>
                <Field label="Which retreat did you attend?">
                  <input className="ssf__input" placeholder="e.g. Retreat 212 (leave blank if not applicable)"
                    value={form.retreat_name} onChange={e => set("retreat_name", e.target.value)} />
                </Field>

                <div className="ssf__location-section">
                  <p className="ssf__location-label">Map Location <span className="ssf__req">*</span></p>
                  <p className="ssf__location-hint">
                    We use approximate city-centre coordinates only — never your exact address.
                  </p>
                  <button type="button" className="ssf__locate-btn" onClick={useMyLocation}>
                    📍 Use My Location (city-level)
                  </button>
                  <div className="ssf__coords-row">
                    <div className="ssf__field">
                      <label className="ssf__label">Latitude</label>
                      <input className="ssf__input" placeholder="e.g. 30.27"
                        value={form.lat} onChange={e => set("lat", e.target.value)} />
                    </div>
                    <div className="ssf__field">
                      <label className="ssf__label">Longitude</label>
                      <input className="ssf__input" placeholder="e.g. -97.74"
                        value={form.lng} onChange={e => set("lng", e.target.value)} />
                    </div>
                  </div>
                  {errors.location && <p className="ssf__field-error">{errors.location}</p>}
                  {form.lat && form.lng && (
                    <p className="ssf__coords-ok">✓ Location set: {parseFloat(form.lat).toFixed(3)}, {parseFloat(form.lng).toFixed(3)}</p>
                  )}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" variants={fadeUp()} initial="hidden" animate="show" exit={{ opacity:0 }}>
                <Field label="Your Rating">
                  <div className="ssf__stars">
                    {RATINGS.map(n => (
                      <button key={n} type="button"
                        className={`ssf__star ${n <= form.rating ? "ssf__star--filled" : ""}`}
                        onClick={() => set("rating", n)}>
                        ★
                      </button>
                    ))}
                    <span className="ssf__star-label">{form.rating}/5</span>
                  </div>
                </Field>

                <Field label="Your Story / Quote" required error={errors.quote}>
                  <textarea className="ssf__textarea" rows={6}
                    placeholder="Share what Forge meant to you. This quote will appear on the community map for others to read. Keep it authentic — there's no wrong way to tell your story."
                    value={form.quote} onChange={e => set("quote", e.target.value)} />
                  <p className="ssf__char">{form.quote.length} characters {form.quote.length < 20 ? "— minimum 20" : "✓"}</p>
                </Field>

                <div className="ssf__privacy-note">
                  <p>🔒 <strong>Privacy:</strong> Only your first name, last initial, city, category, rating, and story quote will appear on the map. Your email and coordinates are kept private.</p>
                </div>

                {submitErr && <p className="ssf__submit-err">{submitErr}</p>}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer navigation */}
        <div className="ssf__footer">
          {step > 1 ? (
            <button type="button" className="ssf__back-btn" onClick={back}>← Back</button>
          ) : (
            <button type="button" className="ssf__cancel-btn" onClick={onClose}>Cancel</button>
          )}
          {step < 3 ? (
            <button type="button" className="ssf__next-btn" onClick={next}>Continue →</button>
          ) : (
            <button type="button" className="ssf__submit-btn" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit My Story"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
